import { useEffect } from 'react';
import { isAppleIOS, setStyleProperty, afterNextPaint } from 'utils';

// Make pages scrollable in IOS Safari so user can hide address bar (full screen not enabled in iPhone)
const handleScrollInstructionVisibility = (handlerName: string) => {
  if (!isAppleIOS()) return;

  const isLandscape = window.matchMedia('(orientation: landscape)').matches;

  if (isLandscape) {
    // LANDSCAPE
    setStyleProperty('--page-margin-top', '175px');
    setStyleProperty('--scroll-instruction-visibility', 'visible');

    console.log(`Landscape mode detected in '${handlerName}' handler. Added top margin and made scroll instruction visible.`);
  } else {
    // PORTRAIT
    // Hide scroll instruction and reset top margin (address bar always visible in portrait, no need for scrolling)
    setStyleProperty('--page-margin-top', '0px');
    setStyleProperty('--scroll-instruction-visibility', 'hidden');

    // Fix iOS scroll position error when flipping back to portrait
    // Only happens if window.scroll is not 0 in landscape before flipping
    if (isAppleIOS()) scrollToWhenReady(0);

    console.log(`Portrait mode detected in '${handlerName}' handler. Removed top margin and hid scroll instruction.`);
  }
};

const scrollToWhenReady = (scrollTo: number) => {
  let attempt = 0;
  const maxAttempts = 10;
  const id = setInterval(() => {
    window.scrollTo(0, scrollTo);
    attempt++;
    // Clear interval after 500ms (10 attempts at 50ms apart)
    // Goal: browser repainting and layout recalculation operations need to finish before scrolling is enabled
    if (attempt >= maxAttempts) clearInterval(id);
  }, 50);
};

const handleOrientationChange = () => afterNextPaint().then(() => handleScrollInstructionVisibility('orientationChange'));

const handleLoad = () => afterNextPaint().then(() => handleScrollInstructionVisibility('load'));

const useIosOrientation = () => {
  const isIOS = isAppleIOS();

  useEffect(() => {
    if (!isIOS) return;

    /*
    * PWA acts like standalone app on IOS. so we do not have address bars
    */
    if (window.navigator.standalone) return;

    // Make pages scrollable in IOS Safari so user can hide address bar (full screen not enabled in iPhone)
    window.addEventListener('load', handleLoad);

    if (window.screen.orientation) {
      window.screen.orientation.addEventListener('change', handleOrientationChange);
    } else {
      window.addEventListener('orientationchange', handleOrientationChange);
    }

    return () => {
      window.removeEventListener('load', handleLoad);

      if (window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', handleOrientationChange);
      } else {
        window.removeEventListener('orientationchange', handleOrientationChange);
      }
    };
  }, [isIOS]);

  return {
    isIOS,
  };
};

export { useIosOrientation };
